import type { ReactElement, Ref } from 'react';
import { formatTimestamp, type Issue } from '@tallyframe/core';
import { Banner } from '../components/ErrorBanner';
import { Button } from '../ui/Button';
import { Stack } from '../ui/Stack';

/** Shown verbatim for a revision conflict (spec §9.2, §10.1): plain and exact,
 *  never the server's own message, which talks about revisions and ids. */
const CONFLICT_MESSAGE = 'This report changed while you were editing. Reload to see the latest version.';

export interface ReportBannersProps {
  /** A save hit a revision conflict. */
  conflict: { onReload(): void } | null;
  /** A change the server rejected, or a save that failed; `onAction` dismisses or retries. */
  problem: {
    kind: 'rejection' | 'save';
    error: { code: string; message: string; issues?: Issue[] };
    onAction(): void;
  } | null;
  exportError: { message: string; onDismiss(): void } | null;
  /** Fetching a version to preview failed; shown only while no preview is on screen. */
  previewError: string | null;
  preview: {
    createdAt: string;
    canRestore: boolean;
    restoring: boolean;
    onRestore(): void;
    onBackToCurrent(): void;
  } | null;
  /** Shown under the preview banner, and only with it. */
  restoreNotice: string | null;
  /** Focus target for the preview banner as it opens. */
  previewBannerRef?: Ref<HTMLDivElement>;
}

/** The editor's banners, in order: conflict, save problem, export error, preview error, preview, restore notice. */
export function ReportBanners(props: ReportBannersProps): ReactElement | null {
  const { conflict, problem, exportError, previewError, preview, restoreNotice } = props;
  const showPreviewError = !preview && previewError;
  const showRestoreNotice = preview && restoreNotice;
  if (!conflict && !problem && !exportError && !showPreviewError && !preview) return null;

  return (
    <Stack gap={3}>
      {conflict ? (
        <Banner
          tone="warn"
          title={CONFLICT_MESSAGE}
          testId="conflict-banner"
          errorCode="REVISION_CONFLICT"
          actions={
            <Button variant="primary" size="sm" onPress={conflict.onReload}>
              Reload
            </Button>
          }
        />
      ) : null}

      {problem ? (
        <Banner
          tone="danger"
          title={problem.kind === 'rejection' ? 'That change was not applied' : 'Your changes could not be saved'}
          issues={problem.error.issues}
          errorCode={problem.error.code}
          testId="error-banner"
          actions={
            <Button size="sm" onPress={problem.onAction}>
              {problem.kind === 'rejection' ? 'Dismiss' : 'Try saving again'}
            </Button>
          }
        >
          <>
            {problem.error.message}
            {problem.kind === 'rejection'
              ? ' The report on screen is unchanged, so it still matches what the server would accept.'
              : ' Your work is still here and still undoable.'}
          </>
        </Banner>
      ) : null}

      {exportError ? (
        <Banner
          tone="danger"
          title="That export failed"
          testId="export-error"
          actions={
            <Button size="sm" onPress={exportError.onDismiss}>
              Dismiss
            </Button>
          }
        >
          {exportError.message}
        </Banner>
      ) : null}

      {showPreviewError ? <Banner tone="danger" title={previewError} testId="preview-error" /> : null}

      {preview ? (
        <div ref={props.previewBannerRef} tabIndex={-1}>
          <Banner
            tone="info"
            title={`Viewing the version from ${formatTimestamp(preview.createdAt)}`}
            testId="preview-banner"
            actions={
              <>
                <Button
                  variant="primary"
                  size="sm"
                  isDisabled={!preview.canRestore || preview.restoring}
                  onPress={(event) => {
                    // The confirmation returns focus to whatever had it as it
                    // opened; a click does not focus a button everywhere (Safari).
                    if (event.target instanceof HTMLElement) event.target.focus();
                    preview.onRestore();
                  }}
                >
                  Restore this version
                </Button>
                <Button size="sm" isDisabled={preview.restoring} onPress={preview.onBackToCurrent}>
                  Back to current
                </Button>
              </>
            }
          />
        </div>
      ) : null}

      {showRestoreNotice ? <Banner tone="warn" title={restoreNotice} testId="restore-notice" /> : null}
    </Stack>
  );
}
